import { IForm } from 'core-components/classes/models/IForm'
import {
  ICrudImplementation,
  TCreateFunction,
  TFetcher,
  TUpdateFunction,
} from 'core-components/types'
import {
  ICountryWithCluster,
  IPartnerTerritory,
} from 'onboarding/classes/models/partnerTerritoryModels'

import { PartnerTerritoryApi } from './PartnerTerritoryApi'

export class LocationTreeFetcher implements ICrudImplementation {
  private skipHpOrgLevel: boolean = false
  private partnerTerritory: IPartnerTerritory
  constructor(options: { skipHpOrgLevel?: boolean; partnerTerritory: IPartnerTerritory }) {
    if (options.skipHpOrgLevel) {
      this.skipHpOrgLevel = true
    }
    this.partnerTerritory = options.partnerTerritory
  }
  public read: TFetcher = async () => {
    const resultArr = []
    const regions = PartnerTerritoryApi.fetchRegions(this.partnerTerritory.regions)
    resultArr.push(
      ...regions.items.map((region) => ({
        ...region,
        Title: region.region,
        Parent: '',
        HasChildren: true,
        Type: 'Region',
        Id: `Region-${region.region}`,
      }))
    )
    const clusters = PartnerTerritoryApi.fetchClusters(this.partnerTerritory.clusters)
    resultArr.push(
      ...clusters.items.map((cluster) => ({
        ...cluster,
        Title: `[Cluster] ${cluster.cluster}`,
        Parent: `Region-${cluster.region}`,
        HasChildren: true,
        Type: 'Cluster',
        Id: `Cluster-${cluster.cluster}`,
      }))
    )
    const markets = PartnerTerritoryApi.fetchMarkets(this.partnerTerritory.markets)
    resultArr.push(
      ...markets.items.map((market) => ({
        ...market,
        Title: market.market,
        Parent: `Region-${market.region}`,
        HasChildren: true,
        Type: 'Market',
        Id: `Market-${market.market}`,
      }))
    )
    if (!this.skipHpOrgLevel) {
      const hpOrgs = PartnerTerritoryApi.fetchHPOrgs(this.partnerTerritory.hpOrgs)
      resultArr.push(
        ...hpOrgs.items.map((org) => ({
          ...org,
          Title: org.hpOrg,
          Parent: `Market-${org.market}`,
          HasChildren: true,
          Type: 'HpOrg',
          Id: `Org-${org.hpOrg}`,
        }))
      )
    }
    const countries = this.partnerTerritory.countries
    resultArr.push(
      ...countries.map((country) => ({
        ...country,
        Title: `${country.name}`,
        Parent: this.skipHpOrgLevel ? `Market-${country.market}` : `Org-${country.hporg}`,
        HasChildren: false,
        Type: 'Country',
        Id: `Country-${country.name}`,
      }))
    )
    for (const country of countries) {
      if (country.clusters) {
        resultArr.push(
          ...country.clusters.map((cluster) => ({
            ...country,
            Title: `${country.name}`,
            Parent: `Cluster-${cluster}`,
            HasChildren: false,
            Type: 'CountryWithParentAsCluster',
            Id: `Country-${country.name}`,
          }))
        )
      }
    }
    const groups = this.getHierarchyGroups(resultArr, 'Parent')
    return {
      groups,
      items: resultArr,
      total: 0,
    }
  }

  public create: TCreateFunction = async (store, models) => {
    return []
  }
  public update: TUpdateFunction = async (store, models) => {
    return []
  }

  private getHierarchyGroups = (items: IForm[], hierarchyField: string) => {
    const hierarchyGroups: any = {}
    const hierarchyGroupsOrdered: any[] = []
    items.forEach((item) => {
      if (!hierarchyGroups[item[hierarchyField]]) {
        hierarchyGroups[item[hierarchyField]] = []
        hierarchyGroupsOrdered.push(hierarchyGroups[item[hierarchyField]])
      }
      hierarchyGroups[item[hierarchyField]].push(item)
    })
    return hierarchyGroupsOrdered
  }
}

export const getCountriesBy = (
  type: 'Region' | 'Market' | 'Cluster' | 'HpOrg' | 'ClusterCountry',
  value: string,
  partnerTerritory: IPartnerTerritory
) => {
  let countries: ICountryWithCluster[]
  let filtered: ICountryWithCluster[]
  const resultArr: ICountryWithCluster[] = []
  switch (type) {
    case 'HpOrg':
      countries = partnerTerritory.countries
      filtered = countries.filter((country) => country.hporg === value)
      return filtered
    case 'ClusterCountry':
      countries = partnerTerritory.countries
      filtered = countries.filter((country) => {
        if (country.clusters) return country.clusters.some((cluster) => cluster === value)
        return false
      })
      return filtered
    case 'Market': {
      const hpOrgs = PartnerTerritoryApi.fetchHPOrgs(partnerTerritory.hpOrgs)
      const filteredOrgs = hpOrgs.items.filter((org) => org.market === value)
      for (const org of filteredOrgs) {
        if (org.hpOrg) {
          const res = getCountriesBy('HpOrg', org.hpOrg, partnerTerritory)
          resultArr.push(...res)
        }
      }
      return resultArr
    }
    case 'Cluster': {
      const clusters = PartnerTerritoryApi.fetchClusters(partnerTerritory.clusters)
      const currentClusters = clusters.items.filter((item) => `[Cluster] ${item.cluster}` === value)
      for (const cluster of currentClusters) {
        if (cluster.cluster) {
          const res = getCountriesBy('ClusterCountry', cluster.cluster, partnerTerritory)
          resultArr.push(...res)
        }
      }
      return resultArr
    }
    case 'Region': {
      const markets = PartnerTerritoryApi.fetchMarkets(partnerTerritory.markets)
      const currentMarkets = markets.items.filter((item) => item.region === value)
      for (const market of currentMarkets) {
        const res = getCountriesBy('Market', market.market, partnerTerritory)
        resultArr.push(...res)
      }
      return resultArr
    }
  }
}
